import { Injectable } from '@angular/core';
import {
  CONTAINER_TYPE_LINK_FREIGHT_TYPE,
  CREATE_ONE_QUANTITY_TYPE,
  GET_ALL_QUANTITY_TYPES,
  GET_ONE_QUANTITY_TYPE,
  UPDATE_ONE_QUANTITY_TYPE,
} from '@queries/quantity-types.graphql';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { ContainerType } from './container-types.types';

function toContainerType(raw: any): ContainerType {
  const freightTypes = (raw.FreightTypes ?? []).map((f: any) => ({ id: f.id, name: f.name }));
  const item = new ContainerType(
    raw.id,
    raw.name,
    raw.quantityInM3 ?? null,
    raw.maxPayloadTons ?? null,
    raw.isActive ?? true,
    freightTypes[0]?.id ?? null,
  );
  item.freightTypes = freightTypes;
  return item;
}

function toNullableNumber(value: unknown): number | null {
  return value === null || value === undefined || value === '' ? null : Number(value);
}

@Injectable({
  providedIn: 'root',
})
export class ContainerTypesService {
  private _item$: BehaviorSubject<ContainerType | null> = new BehaviorSubject(null);
  private _items$: BehaviorSubject<ContainerType[] | null> = new BehaviorSubject(null);
  private _items: ContainerType[] | null = null;

  constructor(private apollo: Apollo) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get containerType$(): Observable<ContainerType> {
    return this._item$.asObservable();
  }

  get containerTypes$(): Observable<ContainerType[]> {
    return this._items$.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  getList(skip: number = 0, take: number = 1000): Observable<ContainerType[]> {
    return this.apollo
      .watchQuery<any>({
        query: GET_ALL_QUANTITY_TYPES,
        variables: {
          skip,
          take,
          where: { isContainer: { equals: true } },
          orderBy: [{ quantityInM3: 'asc' }],
        },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        map(({ data }) => (data.QuantityTypeFindAll ?? []).map(toContainerType)),
        tap((items: ContainerType[]) => {
          this._items = items;
          this._items$.next(items);
        }),
      );
  }

  getById(id: number): Observable<ContainerType> {
    if (!id) {
      const blank = new ContainerType();
      this._item$.next(blank);
      return of(blank);
    }
    return this.apollo
      .watchQuery<any>({
        query: GET_ONE_QUANTITY_TYPE,
        variables: { where: { id } },
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        take(1),
        map(({ data }) => toContainerType(data.QuantityTypeFindOne)),
        tap((item: ContainerType) => this._item$.next(item)),
      );
  }

  search(query: string): Observable<ContainerType[]> {
    const all = this._items ?? [];
    const term = (query ?? '').toLowerCase();
    const items = term ? all.filter((item) => item.name && item.name.toLowerCase().includes(term)) : [...all];
    this._items$.next(items);
    return of(items);
  }

  /**
   * CRUD
   */

  create(record: ContainerType): Observable<ContainerType[]> {
    const maxPayloadTons = toNullableNumber(record.maxPayloadTons);
    return this.apollo
      .mutate<any>({
        mutation: CREATE_ONE_QUANTITY_TYPE,
        variables: {
          data: {
            name: record.name,
            quantityInM3: Number(record.quantityInM3),
            ...(maxPayloadTons !== null ? { maxPayloadTons } : {}),
            isActive: record.isActive,
            isContainer: true,
          },
        },
      })
      .pipe(
        switchMap(({ data }) =>
          record.freightTypeId ? this.link(data.QuantityTypeCreate.id, record.freightTypeId) : of(null),
        ),
        switchMap(() => this.getList().pipe(take(1))),
      );
  }

  update(record: ContainerType): Observable<ContainerType[]> {
    return this.apollo
      .mutate<any>({
        mutation: UPDATE_ONE_QUANTITY_TYPE,
        variables: {
          data: {
            name: { set: record.name },
            quantityInM3: { set: Number(record.quantityInM3) },
            maxPayloadTons: { set: toNullableNumber(record.maxPayloadTons) },
            isActive: { set: record.isActive },
            isContainer: { set: true },
          },
          where: { id: +record.id },
        },
      })
      .pipe(switchMap(() => this.getList().pipe(take(1))));
  }

  linkFreightType(quantityTypeId: number, freightTypeId: number): Observable<ContainerType[]> {
    return this.link(quantityTypeId, freightTypeId).pipe(switchMap(() => this.getList().pipe(take(1))));
  }

  private link(quantityTypeId: number, freightTypeId: number): Observable<unknown> {
    return this.apollo.mutate<any>({
      mutation: CONTAINER_TYPE_LINK_FREIGHT_TYPE,
      variables: { quantityTypeId, freightTypeId },
    });
  }
}
